'use client'

import Link from 'next/link'
import s from './FeaturedProduct.module.scss'
import { useEffect, useRef, useState, useCallback } from 'react'
import LazyImage from '@/components/Common/LazyImage'
import { AnimatePresence, motion } from 'framer-motion'

type Img = {
  imageUrl: string
  filename?: string
  metadata?: { dimensions?: { width?: number; height?: number } }
}
type Data = {
  _type: 'product' | 'story' | string
  title: string
  slug?: string
  client?: { title?: string }
  featuredImage?: Img
  featuredImageList?: Img[]
}
type Props = { data: Data }

const CYCLE_MS = 1000 // velocidad del pase de diapositivas (más pequeño = más rápido)

export default function FeaturedProduct({ data }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const featured = data?.featuredImage
  const list = (data?.featuredImageList || []).filter(Boolean)
  const hasList = list.length > 0

  const [showFeatured, setShowFeatured] = useState(true)
  const [idx, setIdx] = useState(0)
  const [showTitle, setShowTitle] = useState(false)

  // ⭐ detectar dispositivo táctil
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  // ⭐ saber si la tarjeta está en viewport
  const [isInView, setIsInView] = useState(false)
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const href =
    data._type === 'product' ? `/work/${data.slug}` :
    data._type === 'story'   ? `/stories/${data.slug}` : '#'

  const imgW = featured?.metadata?.dimensions?.width ?? 800
  const imgH = featured?.metadata?.dimensions?.height ?? 600
  const aspect = `${imgW} / ${imgH}`

  // ⭐ detectar touch device solo en cliente
  useEffect(() => {
    if (typeof window === 'undefined') return
    const touch =
      'ontouchstart' in window ||
      // @ts-ignore
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 0)
    setIsTouchDevice(!!touch)
  }, [])

  // Preload de la lista para evitar parpadeos
  useEffect(() => {
    if (!hasList) return
    list.forEach((im) => {
      const i = new Image()
      i.src = im.imageUrl
    })
  }, [hasList, list])

  const startCycle = useCallback(() => {
    if (!hasList || timerRef.current) return
    setShowFeatured(false)
    setShowTitle(true)
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % list.length)
    }, CYCLE_MS)
  }, [hasList, list.length])

  const stopCycle = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setIdx(0)
    setShowFeatured(true)
    setShowTitle(false)
  }, [])

  // Limpieza al desmontar
  useEffect(() => () => stopCycle(), [stopCycle])

  // ⭐ Hover solo en desktop (no en touch)
  const onEnter = () => {
    if (isTouchDevice) return
    startCycle()
  }
  const onLeave = () => {
    if (isTouchDevice) return
    stopCycle()
  }

  // ⭐ OBSERVER: saber si el componente está en pantalla
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        setIsInView(entry.isIntersecting)
        // si sale de viewport, paramos ciclo y timeout
        if (!entry.isIntersecting) {
          stopCycle()
          if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current)
            idleTimeoutRef.current = null
          }
        }
      },
      { threshold: 1 } // ~60% visible
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [stopCycle])

  // ⭐ Touch: ciclo solo si está en pantalla 3s sin scroll
  useEffect(() => {
    if (!isTouchDevice || !isInView) return

    const scheduleIdleStart = () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current)
        idleTimeoutRef.current = null
      }

      idleTimeoutRef.current = setTimeout(() => {
        // si sigue en pantalla después de 3s → ciclo
        if (isInView) {
          startCycle()
        }
      }, 1000)
    }

    const handleScroll = () => {
      // si el usuario se mueve → paramos ciclo y reiniciamos contador
      stopCycle()
      scheduleIdleStart()
    }

    // programamos primer idle al entrar en viewport
    scheduleIdleStart()

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current)
        idleTimeoutRef.current = null
      }
    }
  }, [isTouchDevice, isInView, startCycle, stopCycle])

  // Imagen actual
  const currentImg: Img | null =
    showFeatured || !hasList ? featured ?? null : list[idx] ?? null

  return (
    <div
      ref={containerRef}
      className={s.featuredProduct}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      // ❌ Importante: SIN onTouchStart / onTouchEnd
    >
      <Link href={href} className={s.card}>
        <div className={s.imageWrapper} style={{ aspectRatio: aspect }}>
          <AnimatePresence mode="sync">
            {currentImg && (
              <motion.figure
                key={currentImg.imageUrl}
                className={s.figure}
                style={{ aspectRatio: aspect }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <LazyImage
                  src={currentImg.imageUrl}
                  alt={currentImg.filename || "Featured Image"}
                  width={currentImg.metadata?.dimensions?.width || imgW}
                  height={currentImg.metadata?.dimensions?.height || imgH}
                  fill
                  objectFit="cover"
                />
              </motion.figure>
            )}
          </AnimatePresence>
        </div>

        <div className={`${s.title} ${showTitle ? s.show : ''}`}>
          <h3>
            {data.title}
            {data.client?.title && <span translate='no'> for {data.client.title}</span>}
          </h3>
        </div>
      </Link>
    </div>
  )
}